import React from "react";
import {
  Wheat,
  ClipboardList,
  Scale,
  BadgeCheck,
  IndianRupee,
  Warehouse,
  Truck,
  MapPin
} from "lucide-react";
import { useLanguage } from '../context/LanguageContext';

const Agrimarketing = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Cooperative Marketing Societies",

      overview: "Overview",
      overviewText1: "Agri Marketing Societies are farmer-centric institutions under the Cooperative Department that support farmers by providing fair, transparent, and efficient market linkages.",
      overviewText2: "They act as the bridge between farmers and buyers, ensuring that agricultural produce reaches the market safely, scientifically, and profitably.",

      highlightTitle: "Empowering Farmers, Strengthening Local Economies",
      highlightText: "Agri Marketing Societies help farmers secure better prices, reduce distress sales, and connect Tamil Nadu's rural economy to larger markets, all while maintaining transparency and trust.",

      processTitle: "How Agri Marketing Societies Work",
      processSteps: [
        "Entry of Agricultural Produce into Market Chain",
        "Produce Arrival Registration and Allocation of Lots",
        "Weighing & Quality Assessment",
        "Price Discovery Auction",
        "Direct and timely payment to Farmers",
        "Efficient storage and Logistics Management",
        "Streamlined distribution to Buyers"
      ],

      keyStats: "Key Statistics",
      keyStatsNote: "(Value in Crores)",
      keyStatsData: [
        { value: "115", label: "Number of APCMS" },
        { value: "₹1,289.66 Cr", label: "Marketing of Agri Produce" },
        { value: "₹135.25 Cr", label: "Marketing Linkage" },
        { value: "₹114.54 Cr", label: "Produce Pledge Loan" },
        { value: "₹933.24 Cr", label: "Jewel Loan" },
        { value: "₹4,592.21 Cr", label: "Total Business Turnover" },
        { value: "₹838.00 Cr", label: "Distribution of Fertilizers" },
        { value: "₹3.00 Cr", label: "Quality Seeds Sales" },
      ],

      keyActivities: "Key Activities",
      keyActivitiesData: [
        {
          title: "Public Distribution System (PDS)",
          desc: "Most APCMS act as lead societies for lifting essential commodities from TNCSC godowns and even run their own Fair Price Shops."
        },
        {
          title: "Fuel Outlets",
          desc: "Societies run 22 petrol and diesel outlets, selling 21,353.93 kilolitres with a turnover of ₹207.98 crore."
        },
        {
          title: "Digital Marketing",
          desc: "Products like honey and oil are marketed through the CO-OP Mart APP launched on 06.07.2023."
        },
        {
          title: "Retail Operations",
          desc: "APCMS operate Supermarkets and retail outlets to sell groceries and fresh society products."
        },
        {
          title: "Specialized Units",
          desc: "Some societies maintain electronic weighing machines (7 units) to ensure fair transactions."
        },
      ],

      storeLocations: "Store Locations",
      district: "District",
      selectDistrict: "Select District",
      selectBlock: "Select Block",
      filter: "FILTER",

      districts: [
        "Select District",
        "Coimbatore",
        "Chennai",
        "Madurai",
        "Salem",
        "Tiruchirappalli",
        "Tirunelveli",
        "Erode",
        "Vellore"
      ],

      blocks: [
        "Select Block",
        "Primary Cooperative Society",
        "Urban Cooperative Society",
        "Rural Cooperative Society",
        "Consumer Cooperative"
      ]
    },
    ta: {
      title: "கூட்டுறவு சந்தைப்படுத்தல் சங்கங்கள்",

      overview: "கண்ணோட்டம்",
      overviewText1: "விவசாய சந்தைப்படுத்தல் சங்கங்கள் கூட்டுறவுத் துறையின் கீழ் விவசாயிகள் மையப்படுத்தப்பட்ட நிறுவனங்களாகும், அவை நியாயமான, வெளிப்படையான மற்றும் திறமையான சந்தை இணைப்புகளை வழங்குவதன் மூலம் விவசாயிகளுக்கு ஆதரவளிக்கின்றன.",
      overviewText2: "விவசாய உற்பத்திப் பொருட்கள் பாதுகாப்பாக, அறிவியல்பூர்வமாக மற்றும் லாபகரமாக சந்தையை அடைவதை உறுதி செய்து, விவசாயிகள் மற்றும் வாங்குபவர்களுக்கு இடையே பாலமாக செயல்படுகின்றன.",

      highlightTitle: "விவசாயிகளுக்கு அதிகாரம் அளித்தல், உள்ளூர் பொருளாதாரங்களை வலுப்படுத்துதல்",
      highlightText: "விவசாய சந்தைப்படுத்தல் சங்கங்கள் விவசாயிகளுக்கு சிறந்த விலைகளைப் பெற உதவுகின்றன, துயர விற்பனையைக் குறைக்கின்றன, மற்றும் தமிழ்நாட்டின் கிராமப்புற பொருளாதாரத்தை பெரிய சந்தைகளுடன் இணைக்கின்றன, அனைத்தும் வெளிப்படைத்தன்மை மற்றும் நம்பிக்கையை பராமரிக்கின்றன.",

      processTitle: "விவசாய சந்தைப்படுத்தல் சங்கங்கள் எவ்வாறு செயல்படுகின்றன",
      processSteps: [
        "விவசாய உற்பத்திப் பொருட்கள் சந்தை சங்கிலியில் நுழைதல்",
        "உற்பத்திப் பொருள் வருகை பதிவு மற்றும் லாட்டுகள் ஒதுக்கீடு",
        "எடை மற்றும் தர மதிப்பீடு",
        "விலை கண்டுபிடிப்பு ஏலம்",
        "விவசாயிகளுக்கு நேரடி மற்றும் சரியான நேரத்தில் பணம் செலுத்துதல்",
        "திறமையான சேமிப்பு மற்றும் தளவாட மேலாண்மை",
        "வாங்குபவர்களுக்கு ஒழுங்குபடுத்தப்பட்ட விநியோகம்"
      ],

      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      keyStatsNote: "(மதிப்பு கோடியில்)",
      keyStatsData: [
        { value: "115", label: "APCMSகளின் எண்ணிக்கை" },
        { value: "₹1,289.66 Cr", label: "விவசாய பொருட்கள் சந்தைப்படுத்தல்" },
        { value: "₹135.25 Cr", label: "சந்தை இணைப்பு" },
        { value: "₹114.54 Cr", label: "உற்பத்தி அடகு கடன்" },
        { value: "₹933.24 Cr", label: "நகைக் கடன்" },
        { value: "₹4,592.21 Cr", label: "மொத்த வணிக வருவாய்" },
        { value: "₹838.00 Cr", label: "உரங்கள் விநியோகம்" },
        { value: "₹3.00 Cr", label: "தரமான விதைகள் விற்பனை" },
      ],

      keyActivities: "முக்கிய செயல்பாடுகள்",
      keyActivitiesData: [
        {
          title: "பொது விநியோக முறை (PDS)",
          desc: "பெரும்பாலான APCMS கள் TNCSC கிடங்குகளில் இருந்து அத்தியாவசிய பொருட்களை எடுக்கும் முன்னணி சங்கங்களாக செயல்படுகின்றன மற்றும் சொந்த நியாயவிலைக் கடைகளையும் நடத்துகின்றன."
        },
        {
          title: "எரிபொருள் நிலையங்கள்",
          desc: "சங்கங்கள் 22 பெட்ரோல் மற்றும் டீசல் நிலையங்களை நடத்துகின்றன, 21,353.93 கிலோலிட்டர் விற்பனையுடன் ₹207.98 கோடி வருவாய் ஈட்டுகின்றன."
        },
        {
          title: "டிஜிட்டல் சந்தைப்படுத்தல்",
          desc: "தேன் மற்றும் எண்ணெய் போன்ற பொருட்கள் 06.07.2023 அன்று தொடங்கப்பட்ட CO-OP Mart APP மூலம் சந்தைப்படுத்தப்படுகின்றன."
        },
        {
          title: "சில்லறை வணிக நடவடிக்கைகள்",
          desc: "APCMS கள் மளிகை பொருட்கள் மற்றும் புதிய சங்க பொருட்களை விற்க சூப்பர்மார்க்கெட்கள் மற்றும் சில்லறை கடைகளை நடத்துகின்றன."
        },
        {
          title: "சிறப்பு அலகுகள்",
          desc: "சில சங்கங்கள் நியாயமான பரிவர்த்தனைகளை உறுதிப்படுத்த மின்னணு எடை இயந்திரங்களை (7 அலகுகள்) பராமரிக்கின்றன."
        },
      ],

      storeLocations: "கடை இடங்கள்",
      district: "மாவட்டம்",
      selectDistrict: "மாவட்டத்தைத் தேர்ந்தெடுக்கவும்",
      selectBlock: "வட்டத்தைத் தேர்ந்தெடுக்கவும்",
      filter: "வடிகட்டு",

      districts: [
        "மாவட்டத்தைத் தேர்ந்தெடுக்கவும்",
        "கோயம்புத்தூர்",
        "சென்னை",
        "மதுரை",
        "சேலம்",
        "திருச்சிராப்பள்ளி",
        "திருநெல்வேலி",
        "ஈரோடு",
        "வேலூர்"
      ],

      blocks: [
        "வட்டத்தைத் தேர்ந்தெடுக்கவும்",
        "முதன்மை கூட்டுறவு சங்கம்",
        "நகர்ப்புற கூட்டுறவு சங்கம்",
        "கிராமப்புற கூட்டுறவு சங்கம்",
        "நுகர்வோர் கூட்டுறவு"
      ]
    }
  };

  const t = content[language];

  const processSteps = [
    { title: t.processSteps[0], image: "/Images/stage1.png" },
    { title: t.processSteps[1], image: "/Images/stage2.png" },
    { title: t.processSteps[2], image: "/Images/stage3.png" },
    { title: t.processSteps[3], image: "/Images/stage4.png" },
    { title: t.processSteps[4], image: "/Images/stage5.png" },
    { title: t.processSteps[5], image: "/Images/stage6.png" },
    { title: t.processSteps[6], image: "/Images/stage7.png" },
  ];

  return (
    <div className="w-full bg-white">

      {/* ===== Header ===== */}
      <div className="w-full border-b border-gray-200">
        <div className="max-w-7xl mx-auto py-6 text-center">
          <h1 className="text-3xl font-semibold text-blue-900">{t.title}</h1>
        </div>
      </div>

      {/* ===== Overview ===== */}
      <section className="w-full mx-auto px-6 py-10">
        <div className="bg-[#f1f7ff] rounded-xl p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.overview}</h2>
          <div className="flex gap-4">
            <div className="w-1 bg-blue-600 rounded-full mt-1"></div>
            <div>
              <p className="text-gray-800 leading-relaxed mb-4">{t.overviewText1}</p>
              <p className="text-gray-800 leading-relaxed">{t.overviewText2}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Highlight Message ===== */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-semibold text-blue-800 mb-3">{t.highlightTitle}</h3>
            <p className="text-gray-700 max-w-4xl mx-auto">{t.highlightText}</p>
          </div>
        </div>
      </section>

      {/* ===== Connected Process Flow ===== */}
      <section className="py-10 sm:py-16 px-4 sm:px-6 bg-[#f4f8ff] w-full">
        <div className="max-w-7xl mx-auto w-full">
          <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-8 sm:mb-16 text-center">{t.processTitle}</h2>

          {/* All 7 steps in a responsive grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-8 w-full">
            {processSteps.map((step, index) => (
              <div key={index} className="flex flex-col items-center relative w-full">
                <div className="absolute -top-4 -left-4 z-10 bg-blue-600 text-white w-10 h-10 flex items-center justify-center rounded-full font-bold text-lg shadow-lg">
                  {index + 1}
                </div>
                <div className="w-full aspect-square bg-white rounded-xl overflow-hidden shadow-xl border">
                  <img src={step.image} alt={step.title} className="w-full h-full object-cover" />
                </div>
                <p className="mt-4 text-sm sm:text-base font-semibold text-center text-gray-900">{step.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Key Statistics ===== */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">{t.keyStats}</h2>

          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {t.keyStatsData.map((stat, index) => (
              <div key={index} className="bg-teal-700 p-4 rounded-lg shadow-sm text-center">
                <div className="text-lg font-bold text-white mb-2">{stat.value}</div>
                <p className="text-xs text-teal-100 leading-tight">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Key Activities ===== */}
      <section className="py-12 px-6 bg-[#f4f8ff]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">{t.keyActivities}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.keyActivitiesData.map((activity, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <h4 className="font-semibold text-gray-800">{activity.title}</h4>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{activity.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



    </div>
  );
};

export default Agrimarketing;